import { Component, OnDestroy, OnInit } from "@angular/core";
import { Router, NavigationEnd, ActivatedRoute } from "@angular/router";
import { MatDialog } from "@angular/material/dialog";
import { DomSanitizer, SafeResourceUrl } from "@angular/platform-browser";
import { Actions, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import { filter, take } from "rxjs/operators";
import { merge, Subscription } from "rxjs";
import { TranslateService } from "@ngx-translate/core";
import {
  DeleteDigitalCardProfileAction,
  DeleteDigitalCardProfileSuccessAction,
  DELETE_DIGITAL_CARD_PROFILE_FAILURE,
  DELETE_DIGITAL_CARD_PROFILE_SUCCESS,
  GetDigitalCardEmployeeProfilesAction,
  GetDigitalCardProfilesAction,
  SET_DEFAULT_DIGITAL_CARD_PROFILE_FAILURE,
  SET_DEFAULT_DIGITAL_CARD_PROFILE_SUCCESS,
  SetDefaultDigitalCardProfileAction,
  SetDefaultDigitalCardProfileSuccessAction,
} from "../digital-cards.actions";
import {
  getDigitalCardsEmployeeProfilesByTemplateId,
  getDigitalCardsLoading,
  getDigitalCardsLoadingEmployeeTemplateId,
  getDigitalCardsProfiles,
  getDigitalCardsTemplateReadinessById,
} from "../digital-cards.reducer";
import { DigitalCardShareDialogComponent } from "../digital-card-share-dialog/digital-card-share-dialog.component";
import { DigitalCardEntryDialogComponent } from "../digital-card-entry-dialog/digital-card-entry-dialog.component";
import { EmployeePickerItem } from "../../employees/employees.interface";
import { ConfirmationDialogComponent } from "../../shared/confirmation-dialog/confirmation-dialog.component";
import { ConfirmationDialogParams } from "../../shared/confirmation-dialog/confirmation-dialog.interface";
import type {
  DigitalCardEntryDialogData,
  DigitalCardProfileItem,
  DigitalCardProfileReadiness,
  DigitalCardShareDialogData,
} from "../digital-cards.interface";
import { AuthService } from "../../auth/auth.service";
import { ToastrService } from "../../shared/toastr.service";
import {
  canvasListMainPhase,
  type CanvasListMainPhase,
} from "../../shared/canvas-list-view";

const LIST_PATH = "/digital-cards";
const MY_CARDS_PATH = "/my-digital-cards";

@Component({
  selector: "app-digital-cards-list",
  templateUrl: "./digital-cards-list.component.html",
  styleUrls: ["./digital-cards-list.component.scss"],
  standalone: false,
})
export class DigitalCardsListComponent implements OnInit, OnDestroy {
  profiles: DigitalCardProfileItem[] = [];
  loading = true;
  private profilesLoading = true;
  selectedProfile: DigitalCardProfileItem | null = null;
  readOnly = false;
  previewCardProfiles: DigitalCardProfileItem[] = [];
  previewEmployee: EmployeePickerItem | null = null;
  preferredPreviewUserId: string | null = null;
  loadingEmployees = false;
  employeePickerReady = false;
  employeesLoaded = false;
  templateSyncing = false;
  deletingProfileId: string | null = null;
  defaultSettingProfileId: string | null = null;
  entryLinkBusy = false;
  previewIframeSrc: SafeResourceUrl | null = null;
  previewIframeLoading = false;
  private templateReadinessById: Record<string, DigitalCardProfileReadiness> =
    {};
  private employeeProfilesByTemplateId: Record<
    string,
    DigitalCardProfileItem[]
  > = {};
  private routerSub?: Subscription;
  private storeSub?: Subscription;
  private previewIframeRawSrc: string | null = null;

  constructor(
    private store: Store,
    private actions$: Actions,
    private router: Router,
    private route: ActivatedRoute,
    private translate: TranslateService,
    private dialog: MatDialog,
    private sanitizer: DomSanitizer,
    private auth: AuthService,
    private toastr: ToastrService,
  ) {}

  ngOnInit(): void {
    this.readOnly = !!this.route.snapshot.data["readOnly"];
    const preferredUser =
      this.route.snapshot.queryParamMap.get("user")?.trim() ?? "";
    this.preferredPreviewUserId =
      preferredUser.length > 0 ? preferredUser : null;
    this.storeSub = new Subscription();
    this.storeSub.add(
      this.store.select(getDigitalCardsProfiles).subscribe((profiles) => {
        this.profiles = profiles ?? [];
        if (this.profiles.length === 0) {
          this.selectedProfile = null;
          this.previewCardProfiles = [];
          this.employeesLoaded = false;
          this.templateSyncing = false;
          this.syncPageLoading();
          return;
        }
        const selectedId = this.selectedProfile?.profileId;
        const fresh =
          selectedId != null
            ? this.profiles.find((p) => p.profileId === selectedId)
            : undefined;
        const next = fresh ?? this.profiles[0];
        const selectionChanged =
          this.selectedProfile?.profileId !== next.profileId;
        this.selectedProfile = next;
        if (selectionChanged) {
          this.applyEmployeeProfilesCache(next.profileId);
        }
        if (
          !this.readOnly &&
          this.isTemplate(next) &&
          this.shouldFetchEmployeeProfiles(next.profileId)
        ) {
          this.store.dispatch(
            new GetDigitalCardEmployeeProfilesAction({
              templateId: next.profileId,
              mine: this.readOnly,
            }),
          );
        }
        this.refreshSyncFlags();
        this.refreshPreviewIframeSrc();
        this.syncPageLoading();
      }),
    );
    this.storeSub.add(
      this.store.select(getDigitalCardsLoading).subscribe((profilesLoading) => {
        this.profilesLoading = profilesLoading;
        this.syncPageLoading();
      }),
    );
    this.storeSub.add(
      this.store
        .select(getDigitalCardsEmployeeProfilesByTemplateId)
        .subscribe((cache) => {
          this.employeeProfilesByTemplateId = cache ?? {};
          this.applyEmployeeProfilesCache(
            this.selectedProfile?.profileId ?? null,
          );
          this.refreshSyncFlags();
          this.refreshPreviewIframeSrc();
          this.syncPageLoading();
        }),
    );
    this.storeSub.add(
      this.store
        .select(getDigitalCardsLoadingEmployeeTemplateId)
        .subscribe((id) => {
          this.loadingEmployees =
            id != null &&
            id === this.selectedProfile?.profileId &&
            !this.readOnly;
          if (
            !this.loadingEmployees &&
            this.selectedProfile &&
            this.isTemplate(this.selectedProfile) &&
            !this.readOnly &&
            !(
              this.selectedProfile.profileId in
              this.employeeProfilesByTemplateId
            )
          ) {
            this.previewCardProfiles = [];
            this.employeesLoaded = true;
          }
          this.refreshSyncFlags();
          this.syncPageLoading();
        }),
    );
    this.storeSub.add(
      this.store
        .select(getDigitalCardsTemplateReadinessById)
        .subscribe((byId) => {
          this.templateReadinessById = byId ?? {};
          this.refreshSyncFlags();
          this.refreshPreviewIframeSrc();
        }),
    );
    this.loadProfiles();
    this.routerSub = this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe(() => {
        const path = this.router.url.split("?")[0].replace(/\/$/, "");
        if (path === LIST_PATH || path === MY_CARDS_PATH) {
          this.loadProfiles();
        }
      });
  }

  ngOnDestroy(): void {
    this.routerSub?.unsubscribe();
    this.storeSub?.unsubscribe();
  }

  loadProfiles(): void {
    this.previewIframeRawSrc = null;
    this.previewIframeSrc = null;
    this.employeesLoaded = false;
    this.templateSyncing = false;
    this.profilesLoading = true;
    this.syncPageLoading();
    this.store.dispatch(
      new GetDigitalCardProfilesAction({
        templatesOnly: !this.readOnly,
        mine: this.readOnly,
      }),
    );
  }

  private syncPageLoading(): void {
    this.loading =
      this.profilesLoading ||
      (!this.readOnly &&
        !!this.selectedProfile &&
        this.isTemplate(this.selectedProfile) &&
        !this.employeePickerReady);
  }

  isTemplate(profile: DigitalCardProfileItem): boolean {
    return profile.email == null;
  }

  isPersonalCardReady(profile: DigitalCardProfileItem): boolean {
    return (
      profile.status === "published" &&
      !!profile.publicUrl?.trim() &&
      !!profile.currentRenderId?.trim()
    );
  }

  isTemplateSyncing(profile: DigitalCardProfileItem): boolean {
    if (profile.email != null) {
      return (
        profile.status === "published" && !this.isPersonalCardReady(profile)
      );
    }
    if (
      this.loadingEmployees &&
      profile.profileId === this.selectedProfile?.profileId
    ) {
      return true;
    }
    return this.templateReadinessById[profile.profileId]?.status === "pending";
  }

  get showIframe(): boolean {
    if (!this.selectedProfile || !this.previewIframeSrc) return false;
    if (!this.isTemplate(this.selectedProfile)) {
      return this.isPersonalCardReady(this.selectedProfile);
    }
    return this.employeeCardReady;
  }

  get pickerTemplateContextId(): string {
    return this.readOnly ? "digital-cards-my" : "digital-cards";
  }

  get listBusy(): boolean {
    return this.loading;
  }

  get contentReady(): boolean {
    if (this.showEmptyEmployees || this.showNotPublished) {
      return true;
    }
    return this.showIframe && !this.previewIframeLoading;
  }

  get mainPhase(): CanvasListMainPhase {
    return canvasListMainPhase({
      listBusy: this.listBusy,
      hasSelection: !!this.selectedProfile,
      contentReady: this.contentReady,
    });
  }

  get mainReady(): boolean {
    return this.mainPhase === "ready";
  }

  get shareTarget(): DigitalCardProfileItem | null {
    if (!this.mainReady) {
      return null;
    }
    const selected = this.selectedProfile;
    if (!selected) {
      return null;
    }
    const target = this.isTemplate(selected)
      ? this.getPreviewCardProfile()
      : selected;
    if (
      !target ||
      this.isTemplate(target) ||
      !this.isPersonalCardReady(target)
    ) {
      return null;
    }
    return target;
  }

  get showSyncing(): boolean {
    if (!this.selectedProfile || this.showIframe) return false;
    if (!this.isTemplate(this.selectedProfile)) {
      return this.isTemplateSyncing(this.selectedProfile);
    }
    return (
      this.templateSyncing || this.loadingEmployees || !this.employeesLoaded
    );
  }

  get showWaitingPicker(): boolean {
    return (
      !!this.selectedProfile &&
      !this.readOnly &&
      this.isTemplate(this.selectedProfile) &&
      this.employeesLoaded &&
      !this.templateSyncing &&
      this.previewCardProfiles.length > 0 &&
      !this.previewEmployee
    );
  }

  get showEmptyEmployees(): boolean {
    return (
      !!this.selectedProfile &&
      !this.readOnly &&
      this.isTemplate(this.selectedProfile) &&
      this.employeesLoaded &&
      !this.templateSyncing &&
      this.previewCardProfiles.length === 0
    );
  }

  get showNotPublished(): boolean {
    if (!this.selectedProfile) return false;
    if (
      this.showIframe ||
      this.showSyncing ||
      this.showEmptyEmployees ||
      this.showWaitingPicker
    ) {
      return false;
    }
    if (!this.isTemplate(this.selectedProfile)) {
      return true;
    }
    return this.employeesLoaded && !this.templateSyncing;
  }

  get employeeCardReady(): boolean {
    const email = this.previewEmployee?.email?.trim().toLowerCase();
    if (!email) return false;
    const card = this.previewCardProfiles.find(
      (p) => (p.email ?? "").trim().toLowerCase() === email,
    );
    return (
      !!card &&
      card.status === "published" &&
      !!card.publicUrl?.trim() &&
      !!card.currentRenderId?.trim()
    );
  }

  onEmployeePickerInitialResolved(): void {
    this.employeePickerReady = true;
    this.syncPageLoading();
    this.refreshPreviewIframeSrc();
  }

  selectCard(profile: DigitalCardProfileItem): void {
    if (this.selectedProfile?.profileId === profile.profileId) {
      return;
    }
    this.previewIframeRawSrc = null;
    this.previewIframeSrc = null;
    this.previewIframeLoading = false;
    this.selectedProfile = profile;
    this.applyEmployeeProfilesCache(profile.profileId);
    this.refreshSyncFlags();
    if (!this.readOnly && this.isTemplate(profile)) {
      if (this.shouldFetchEmployeeProfiles(profile.profileId)) {
        this.store.dispatch(
          new GetDigitalCardEmployeeProfilesAction({
            templateId: profile.profileId,
            mine: this.readOnly,
          }),
        );
      }
    } else {
      this.previewCardProfiles = [];
      this.employeesLoaded = true;
    }
    this.refreshPreviewIframeSrc();
    this.syncPageLoading();
  }

  private shouldFetchEmployeeProfiles(templateId: string): boolean {
    if (!(templateId in this.employeeProfilesByTemplateId)) {
      return true;
    }
    const readiness = this.templateReadinessById[templateId];
    return !readiness || readiness.status === "pending";
  }

  private applyEmployeeProfilesCache(templateId: string | null): void {
    if (
      !templateId ||
      !this.selectedProfile ||
      !this.isTemplate(this.selectedProfile) ||
      this.readOnly
    ) {
      return;
    }
    if (templateId in this.employeeProfilesByTemplateId) {
      this.previewCardProfiles =
        this.employeeProfilesByTemplateId[templateId] ?? [];
      this.employeesLoaded = true;
      return;
    }
    this.previewCardProfiles = [];
    this.employeesLoaded = false;
  }

  onPreviewEmployeeChange(emp: EmployeePickerItem | null): void {
    this.previewEmployee = emp;
    this.employeePickerReady = true;
    this.syncPageLoading();
    this.refreshPreviewIframeSrc();
  }

  onPreviewIframeLoad(): void {
    this.previewIframeLoading = false;
  }

  getPreviewCardProfile(): DigitalCardProfileItem | null {
    const email = this.previewEmployee?.email?.trim().toLowerCase();
    if (!email) return null;
    return (
      this.previewCardProfiles.find(
        (p) => (p.email ?? "").trim().toLowerCase() === email,
      ) ?? null
    );
  }

  getActionsTargetProfile(): DigitalCardProfileItem | null {
    if (!this.selectedProfile) return null;
    if (this.isTemplate(this.selectedProfile)) {
      return this.getPreviewCardProfile() ?? this.selectedProfile;
    }
    return this.selectedProfile;
  }

  isSelected(profile: DigitalCardProfileItem): boolean {
    return this.selectedProfile?.profileId === profile.profileId;
  }

  addCard(): void {
    void this.router.navigate(["/digital-cards/create"]);
  }

  editCard(profile: DigitalCardProfileItem): void {
    void this.router.navigate(["/digital-cards", profile.profileId, "edit"]);
  }

  openCardUrl(): void {
    const url = this.shareTarget?.publicUrl?.trim();
    if (!url) {
      return;
    }
    window.open(url, "_blank", "noopener,noreferrer");
  }

  openShare(): void {
    const target = this.shareTarget;
    if (!target) {
      return;
    }
    const data: DigitalCardShareDialogData = {
      shareUrl: target.publicUrl.trim(),
      displayName: target.displayName ?? "",
      profileId: target.profileId.trim(),
      email:
        target.email?.trim() || this.previewEmployee?.email?.trim() || null,
    };
    this.dialog.open(DigitalCardShareDialogComponent, {
      width: "400px",
      panelClass: "app-dialog-panel",
      data,
    });
  }

  getStatusLabel(status: string, profile: DigitalCardProfileItem): string {
    if (this.isTemplateSyncing(profile)) {
      return this.translate.instant("digitalCards.status.syncing") as string;
    }
    if (status === "published") {
      return this.translate.instant("digitalCards.status.published") as string;
    }
    if (status === "draft") {
      return this.translate.instant("digitalCards.status.draft") as string;
    }
    return status;
  }

  canShowEntryLink(): boolean {
    const oi = this.auth.appUser?.optionalInterfaces;
    return oi?.appleWallet || oi?.googleWallet;
  }

  openEntryDialog(): void {
    if (this.entryLinkBusy) return;
    const p = this.getActionsTargetProfile();
    const profileId = p?.profileId?.trim() ?? "";
    const canOpen =
      !!p &&
      !this.isTemplate(p) &&
      p.status === "published" &&
      !!profileId &&
      !!p.currentRenderId?.trim();
    if (!canOpen) {
      this.toastr.warning(
        this.translate.instant(
          "digitalCards.sources.noPublishedCard",
        ) as string,
      );
      return;
    }
    this.entryLinkBusy = true;
    const email =
      this.previewEmployee?.email?.trim() || p?.email?.trim() || null;
    const name =
      this.previewEmployee?.givenName?.trim() ||
      this.previewEmployee?.fullName?.trim() ||
      p?.displayName?.trim() ||
      "";
    const data: DigitalCardEntryDialogData = {
      profileId,
      name,
      email,
      mine: this.readOnly,
    };
    this.dialog.open(DigitalCardEntryDialogComponent, {
      width: "420px",
      panelClass: "app-dialog-panel",
      data,
    });
    this.entryLinkBusy = false;
  }

  deleteCard(profile: DigitalCardProfileItem, event: Event): void {
    event.stopPropagation();
    if (this.profiles.length <= 1) return;
    const name = profile.displayName || profile.profileId;
    const data: ConfirmationDialogParams = {
      title: "digitalCards.deleteConfirmTitle",
      body: (
        this.translate.instant("digitalCards.deleteConfirmBody") as string
      ).replace("{{name}}", name),
      confirmButton: "digitalCards.delete",
    };
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      width: "400px",
      data,
    });
    dialogRef.afterClosed().subscribe((confirmed) => {
      if (!confirmed) return;
      const pid = profile.profileId;
      this.deletingProfileId = pid;
      this.store.dispatch(new DeleteDigitalCardProfileAction(pid));
      const done = merge(
        this.actions$.pipe(
          ofType(DELETE_DIGITAL_CARD_PROFILE_SUCCESS),
          filter(
            (a: DeleteDigitalCardProfileSuccessAction) => a.payload === pid,
          ),
        ),
        this.actions$.pipe(ofType(DELETE_DIGITAL_CARD_PROFILE_FAILURE)),
      )
        .pipe(take(1))
        .subscribe(() => {
          this.deletingProfileId = null;
        });
      this.storeSub?.add(done);
    });
  }

  setDefault(profile: DigitalCardProfileItem): void {
    if (profile.isDefault || this.defaultSettingProfileId) return;
    const data: ConfirmationDialogParams = {
      title: "digitalCards.default.setDefaultConfirmation",
      body: "digitalCards.default.setDefaultBody",
      confirmButton: "digitalCards.default.setDefaultButton",
    };
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      width: "400px",
      data,
    });
    dialogRef.afterClosed().subscribe((confirmed) => {
      if (!confirmed) return;
      const pid = profile.profileId;
      this.defaultSettingProfileId = pid;
      this.store.dispatch(new SetDefaultDigitalCardProfileAction(pid));
      const done = merge(
        this.actions$.pipe(
          ofType(SET_DEFAULT_DIGITAL_CARD_PROFILE_SUCCESS),
          filter(
            (a: SetDefaultDigitalCardProfileSuccessAction) => a.payload === pid,
          ),
        ),
        this.actions$.pipe(ofType(SET_DEFAULT_DIGITAL_CARD_PROFILE_FAILURE)),
      )
        .pipe(take(1))
        .subscribe(() => {
          this.defaultSettingProfileId = null;
        });
      this.storeSub?.add(done);
    });
  }

  private refreshSyncFlags(): void {
    const profile = this.selectedProfile;
    if (!profile) {
      this.templateSyncing = false;
      return;
    }
    if (!this.isTemplate(profile)) {
      this.templateSyncing = this.isTemplateSyncing(profile);
      return;
    }
    const readiness = this.templateReadinessById[profile.profileId];
    this.templateSyncing =
      this.loadingEmployees || readiness?.status === "pending";
  }

  private refreshPreviewIframeSrc(): void {
    const profile = this.selectedProfile;
    if (!profile) {
      this.previewIframeRawSrc = null;
      this.previewIframeSrc = null;
      this.previewIframeLoading = false;
      return;
    }
    let url: string | null = null;
    if (this.isTemplate(profile)) {
      if (!this.employeeCardReady) {
        this.previewIframeRawSrc = null;
        this.previewIframeSrc = null;
        this.previewIframeLoading = false;
        return;
      }
      url = this.getPreviewCardProfile()?.publicUrl ?? null;
    } else {
      url = profile.publicUrl ?? null;
    }
    if (!url?.trim()) {
      this.previewIframeRawSrc = null;
      this.previewIframeSrc = null;
      this.previewIframeLoading = false;
      return;
    }
    const target = this.getActionsTargetProfile();
    const renderId = target?.currentRenderId?.trim() || "0";
    const sep = url.includes("?") ? "&" : "?";
    const nextRaw = `${url}${sep}previewMode=1&v=${encodeURIComponent(renderId)}`;
    if (this.previewIframeRawSrc === nextRaw) {
      return;
    }
    this.previewIframeRawSrc = nextRaw;
    this.previewIframeLoading = true;
    this.previewIframeSrc =
      this.sanitizer.bypassSecurityTrustResourceUrl(nextRaw);
  }
}
